import { Button, Input, Label } from "@exhousing/design-system";
import { Plus, UserPlus } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Drawer } from "@/components/Drawer";
import { NotesTimeline } from "@/components/crm/NotesTimeline";
import {
  useAccount,
  useAddAccountContact,
  useUpdateAccount,
} from "@/hooks/api/useCrm";
import type { components } from "@/types/api";

type Account = components["schemas"]["AccountResponse"];

function AccountBody({ account }: { account: Account }) {
  const update = useUpdateAccount(account.id);
  const addContact = useAddAccountContact(account.id);

  const initial = {
    name: account.name ?? "",
    email: account.email ?? "",
    telephone: account.telephone ?? "",
    website: account.website ?? "",
    street: account.street ?? "",
    house_number: account.house_number ?? "",
    postal_code: account.postal_code ?? "",
    city: account.city ?? "",
    notes: account.notes ?? "",
  };
  const [form, setForm] = useState(initial);
  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));
  const dirty = (Object.keys(initial) as (keyof typeof form)[]).some(
    (k) => form[k] !== initial[k],
  );

  const [showContact, setShowContact] = useState(false);
  const [contact, setContact] = useState({ first_name: "", last_name: "", role_title: "", email: "", telephone: "" });

  const save = () => {
    if (!form.name.trim()) return;
    update.mutate(
      {
        name: form.name,
        email: form.email || null,
        telephone: form.telephone || null,
        website: form.website || null,
        street: form.street || null,
        house_number: form.house_number || null,
        postal_code: form.postal_code || null,
        city: form.city || null,
        notes: form.notes || null,
      },
      {
        onSuccess: () => toast.success("Account saved"),
        onError: () => toast.error("Could not save account"),
      },
    );
  };

  const submitContact = (e: React.FormEvent) => {
    e.preventDefault();
    addContact.mutate(
      {
        first_name: contact.first_name || null,
        last_name: contact.last_name || null,
        role_title: contact.role_title || null,
        email: contact.email || null,
        telephone: contact.telephone || null,
      },
      {
        onSuccess: () => {
          toast.success("Contact added");
          setShowContact(false);
          setContact({ first_name: "", last_name: "", role_title: "", email: "", telephone: "" });
        },
        onError: () => toast.error("Could not add contact"),
      },
    );
  };

  return (
    <div className="space-y-6">
      <section className="space-y-3">
        <Field label="Company name" value={form.name} onChange={(v) => set("name", v)} />
        <div className="grid grid-cols-2 gap-3">
          <Field label="Email" value={form.email} onChange={(v) => set("email", v)} />
          <Field label="Phone" value={form.telephone} onChange={(v) => set("telephone", v)} />
          <Field label="Website" value={form.website} onChange={(v) => set("website", v)} />
          <Field label="City" value={form.city} onChange={(v) => set("city", v)} />
          <Field label="Street" value={form.street} onChange={(v) => set("street", v)} />
          <Field label="No." value={form.house_number} onChange={(v) => set("house_number", v)} />
          <Field label="Postal code" value={form.postal_code} onChange={(v) => set("postal_code", v)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="acc-notes">Notes</Label>
          <textarea
            id="acc-notes"
            rows={3}
            value={form.notes}
            onChange={(e) => set("notes", e.target.value)}
            className="min-h-16 w-full resize-y rounded-md border border-input bg-card p-2.5 text-sm outline-none focus-visible:ring-2 focus-visible:ring-ring"
          />
        </div>
        <Button size="sm" disabled={update.isPending || !form.name.trim() || !dirty} onClick={save}>
          {update.isPending ? "Saving…" : "Save"}
        </Button>
      </section>

      <section className="space-y-3 border-t border-border pt-5">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-medium">Contacts</h3>
          <Button size="sm" variant="ghost" onClick={() => setShowContact((s) => !s)}>
            <UserPlus className="size-4" />
            Add
          </Button>
        </div>
        {showContact && (
          <form onSubmit={submitContact} className="grid grid-cols-2 gap-2 rounded-lg border border-border p-3">
            <Input placeholder="First name" value={contact.first_name} onChange={(e) => setContact({ ...contact, first_name: e.target.value })} />
            <Input placeholder="Last name" value={contact.last_name} onChange={(e) => setContact({ ...contact, last_name: e.target.value })} />
            <Input placeholder="Role" value={contact.role_title} onChange={(e) => setContact({ ...contact, role_title: e.target.value })} />
            <Input placeholder="Email" value={contact.email} onChange={(e) => setContact({ ...contact, email: e.target.value })} />
            <Input placeholder="Phone" value={contact.telephone} onChange={(e) => setContact({ ...contact, telephone: e.target.value })} />
            <Button type="submit" size="sm" disabled={addContact.isPending}>
              <Plus className="size-4" />
              Add contact
            </Button>
          </form>
        )}
        {account.contacts.length === 0 ? (
          <p className="text-sm text-muted-foreground">No contacts yet.</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {account.contacts.map((c) => (
              <li key={c.id} className="py-2">
                <p className="text-sm font-medium">
                  {[c.first_name, c.last_name].filter(Boolean).join(" ") || "—"}
                  {c.role_title ? <span className="font-normal text-muted-foreground"> · {c.role_title}</span> : null}
                </p>
                <p className="text-xs text-muted-foreground">
                  {[c.email, c.telephone].filter(Boolean).join(" · ") || "—"}
                </p>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="space-y-3 border-t border-border pt-5">
        <h3 className="text-sm font-medium">Activity</h3>
        <NotesTimeline subjectType="account" subjectId={account.id} />
      </section>
    </div>
  );
}

function Field({ label, value, onChange }: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Input value={value} onChange={(e) => onChange(e.target.value)} />
    </div>
  );
}

export function AccountDrawer({
  accountId,
  onClose,
}: {
  accountId: string | null;
  onClose: () => void;
}) {
  const { data: account, isLoading } = useAccount(accountId ?? undefined);

  return (
    <Drawer
      open={Boolean(accountId)}
      onClose={onClose}
      title={account?.name ?? "Account"}
      subtitle={account?.city ?? undefined}
    >
      {isLoading || !account ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <AccountBody key={account.id} account={account} />
      )}
    </Drawer>
  );
}
